import React, { useState, useEffect, useCallback } from "react";
import { Key, Plus, RefreshCw, Trash2, Edit2, Check, X, Search, Copy } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_COLORS = {
  available: { bg: "#e8f5e9", color: "#2e7d32", label: "Disponibile" },
  sold:      { bg: "#fff3e0", color: "#e65100", label: "Venduta" },
  active:    { bg: "#e3f2fd", color: "#1565c0", label: "Attiva" },
  inactive:  { bg: "#f5f5f5", color: "#616161", label: "Inattiva" },
  expired:   { bg: "#fce4ec", color: "#c62828", label: "Scaduta" },
  returned:  { bg: "#f3e5f5", color: "#6a1b9a", label: "Rimborsata" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#eee", color: "#333", label: status };
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      fontSize: "0.75rem", fontWeight: 700, background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
};

const EMPTY_FORM = {
  product_slug: "", product_name: "", order_id: "",
  customer_email: "", customer_name: "",
  max_activations: 1, expiration_date: "", notes: "",
};

export default function LicensesAdmin({ authHeaders }) {
  const adminKey = useCallback(() => authHeaders().headers["X-Admin-Key"] || "", [authHeaders]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (search) params.set("search", search);
      const res = await fetch(`${API}/api/admin/licenses?${params}`, {
        headers: { "x-admin-key": adminKey() },
      });
      if (!res.ok) throw new Error(await res.text());
      setLicenses(await res.json());
    } catch (e) {
      toast.error("Errore caricamento licenze: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search, adminKey]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/licenses`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-admin-key": adminKey() },
        body: JSON.stringify({ ...form, max_activations: Number(form.max_activations) }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Licenza creata");
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error("Errore: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (key) => {
    setSaving(true);
    try {
      const payload = {};
      if (editForm.status !== undefined) payload.status = editForm.status;
      if (editForm.expiration_date !== undefined) payload.expiration_date = editForm.expiration_date || null;
      if (editForm.notes !== undefined) payload.notes = editForm.notes;
      if (editForm.max_activations !== undefined) payload.max_activations = Number(editForm.max_activations);
      const res = await fetch(`${API}/api/admin/licenses/${key}`, {
        method: "PUT",
        headers: { "content-type": "application/json", "x-admin-key": adminKey() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Aggiornata");
      setEditKey(null);
      load();
    } catch (e) {
      toast.error("Errore: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Eliminare la licenza ${key}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/licenses/${key}`, {
        method: "DELETE",
        headers: { "x-admin-key": adminKey() },
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Eliminata");
      load();
    } catch (e) {
      toast.error("Errore: " + e.message);
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("Chiave copiata");
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f5f5f5", borderRadius: 8, padding: "6px 12px", flex: 1, minWidth: 180 }}>
          <Search size={15} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca chiave, email, dominio…"
            style={{ border: "none", background: "transparent", outline: "none", width: "100%" }}
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: "0.9rem" }}
        >
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_COLORS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button onClick={load} style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #ddd", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={14} /> Aggiorna
        </button>
        <button
          onClick={() => setShowCreate(true)}
          style={{ padding: "7px 14px", borderRadius: 8, background: "#1a1a2e", color: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}
        >
          <Plus size={14} /> Nuova licenza
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: "1rem" }}>Genera nuova licenza</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {[
              { key: "product_name", label: "Nome prodotto" },
              { key: "product_slug", label: "Slug prodotto" },
              { key: "order_id", label: "ID ordine" },
              { key: "customer_email", label: "Email cliente" },
              { key: "customer_name", label: "Nome cliente" },
              { key: "expiration_date", label: "Scadenza (ISO, es: 2027-01-01)" },
              { key: "notes", label: "Note" },
            ].map(({ key, label }) => (
              <div key={key}>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
                <input
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #ccc", boxSizing: "border-box" }}
                />
              </div>
            ))}
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>Max attivazioni</label>
              <input
                type="number" min={1} value={form.max_activations}
                onChange={e => setForm(f => ({ ...f, max_activations: e.target.value }))}
                style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #ccc", boxSizing: "border-box" }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button onClick={handleCreate} disabled={saving}
              style={{ padding: "8px 18px", borderRadius: 8, background: "#1a1a2e", color: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
              <Key size={14} /> {saving ? "Creazione…" : "Genera chiave"}
            </button>
            <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
              style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#888" }}>Caricamento…</div>
      ) : licenses.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
          <Key size={40} strokeWidth={1} style={{ marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
          Nessuna licenza trovata
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ background: "#f0f0f0" }}>
                {["Chiave", "Prodotto", "Cliente / Email", "Dominio", "Stato", "Scadenza", "Azioni"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {licenses.map(lic => (
                <tr key={lic.key} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "8px 12px", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: "0.8rem" }}>{lic.key}</span>
                    <button onClick={() => copyKey(lic.key)} style={{ background: "none", border: "none", cursor: "pointer", marginLeft: 4 }} title="Copia">
                      <Copy size={12} />
                    </button>
                  </td>
                  <td style={{ padding: "8px 12px" }}>{lic.product_name || lic.product_slug || "—"}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <div>{lic.customer_name || "—"}</div>
                    <div style={{ color: "#888", fontSize: "0.78rem" }}>{lic.customer_email}</div>
                  </td>
                  <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {lic.domain || <span style={{ color: "#bbb" }}>—</span>}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    {editKey === lic.key ? (
                      <select
                        value={editForm.status ?? lic.status}
                        onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                        style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc" }}
                      >
                        {Object.entries(STATUS_COLORS).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    ) : (
                      <StatusBadge status={lic.status} />
                    )}
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {editKey === lic.key ? (
                      <input
                        value={editForm.expiration_date ?? (lic.expiration_date ? lic.expiration_date.slice(0, 10) : "")}
                        onChange={e => setEditForm(f => ({ ...f, expiration_date: e.target.value }))}
                        placeholder="AAAA-MM-GG"
                        style={{ width: 120, padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc" }}
                      />
                    ) : (
                      lic.expiration_date ? lic.expiration_date.slice(0, 10) : <span style={{ color: "#bbb" }}>Mai</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {editKey === lic.key ? (
                      <>
                        <button onClick={() => handleUpdate(lic.key)} disabled={saving}
                          style={{ background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", marginRight: 4 }}>
                          <Check size={13} />
                        </button>
                        <button onClick={() => setEditKey(null)}
                          style={{ background: "#eee", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
                          <X size={13} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditKey(lic.key); setEditForm({ status: lic.status, expiration_date: lic.expiration_date ? lic.expiration_date.slice(0, 10) : "" }); }}
                          style={{ background: "#f0f0f0", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", marginRight: 4 }} title="Modifica">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(lic.key)}
                          style={{ background: "#fce4ec", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#c62828" }} title="Elimina">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "10px 12px", color: "#888", fontSize: "0.8rem" }}>
            {licenses.length} licenz{licenses.length === 1 ? "a" : "e"}
          </div>
        </div>
      )}
    </div>
  );
}
